import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import homeFaq from '../content/homeFaq';

/**
 * Schema Markup (JSON-LD) pentru SEO
 * Include: FAQPage, LocalBusiness, Service, BreadcrumbList
 */
const SchemaMarkup = () => {
  const location = useLocation();

  // Skip pe paginile admin
  const isAdminPage = location.pathname.startsWith('/admin');

  useEffect(() => {
    if (isAdminPage) return;

    // Curăță schema markup existente (evită duplicare)
    const existingScripts = document.querySelectorAll('script[type="application/ld+json"][data-schema]');
    existingScripts.forEach(script => script.remove());

    // 1. LocalBusiness Schema
    const localBusinessSchema = {
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      "name": "CF-Extras.ro",
      "description": "Serviciu online pentru obținerea extraselor de carte funciară și planurilor cadastrale de la ANCPI",
      "url": "https://cf-extras.ro",
      "logo": "https://cf-extras.ro/logo-cf.png",
      "image": "https://cf-extras.ro/og-image-v2.png",
      "telephone": "+40741909674",
      "email": "contact@cf-extras.ro",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "Str. Fratii Golescu 4",
        "addressLocality": "București",
        "addressRegion": "Sector 1",
        "postalCode": "010494",
        "addressCountry": "RO"
      },
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": "44.4396",
        "longitude": "26.0963"
      },
      "openingHoursSpecification": {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
        "opens": "00:00",
        "closes": "23:59"
      },
      "priceRange": "84-200 RON",
      "currenciesAccepted": "RON",
      "paymentAccepted": "Credit Card",
      "areaServed": {
        "@type": "Country",
        "name": "Romania"
      }
    };

    // 2. Service Schema
    const serviceSchema = {
      "@context": "https://schema.org",
      "@type": "Service",
      "serviceType": "Extras Carte Funciară Online",
      "name": "Extras Carte Funciară și Plan Cadastral Online",
      "description": "Obține extrase de carte funciară și planuri cadastrale oficial emise de ANCPI, livrate pe email în minute",
      "provider": {
        "@type": "Organization",
        "name": "BITCRAFTERS SRL",
        "url": "https://cf-extras.ro"
      },
      "areaServed": {
        "@type": "Country",
        "name": "Romania"
      },
      "hasOfferCatalog": {
        "@type": "OfferCatalog",
        "name": "Servicii Carte Funciară",
        "itemListElement": [
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "Extras Carte Funciară"
            },
            "price": "99.86",
            "priceCurrency": "RON"
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "Extras pe Ortofotoplan (Plan Cadastral)"
            },
            "price": "99.70",
            "priceCurrency": "RON"
          }
        ]
      }
    };

    // 3. FAQPage Schema
    const faqSchema = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": homeFaq.map(f => ({
        "@type": "Question",
        "name": f.question,
        "acceptedAnswer": { "@type": "Answer", "text": f.answer }
      }))
    };

    // 4. WebSite Schema (pentru search box în Google)
    const websiteSchema = {
      "@context": "https://schema.org",
      "@type": "WebSite",
      "name": "CF-Extras.ro",
      "url": "https://cf-extras.ro/",
      "description": "Extras Carte Funciară Online - Livrare pe Email în 5 Minute",
      "publisher": {
        "@type": "Organization",
        "name": "BITCRAFTERS SRL"
      }
    };

    // Injectare în DOM
    const schemas = [
      { id: 'local-business', data: localBusinessSchema },
      { id: 'service', data: serviceSchema },
      // FAQPage DOAR pe homepage (unde e afișat FAQ-ul) - nu pe juridice/orașe
      ...(location.pathname === '/' ? [{ id: 'faq', data: faqSchema }] : []),
      { id: 'website', data: websiteSchema }
    ];

    schemas.forEach(schema => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.setAttribute('data-schema', schema.id);
      script.textContent = JSON.stringify(schema.data);
      document.head.appendChild(script);
    });

    // Cleanup la unmount
    return () => {
      const scripts = document.querySelectorAll('script[type="application/ld+json"][data-schema]');
      scripts.forEach(script => script.remove());
    };
  }, [isAdminPage, location.pathname]);

  return null;
};

export default SchemaMarkup;
